import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import { ProductReferences } from 'common/constants/enums';
import { useUpdateTitle } from 'lib/dom/useUpdateTitle';
import { noOpFunction } from 'lib/noOpFunction';
import { getProductName } from 'lib/productInfo/getProductName';
import { SessionsScreen } from 'sleepio/components/screens/SessionsScreen';
import { setIsHandoffAuthenticated } from 'state/user/actions';
import { getIsHandoffAuthenticated } from 'state/user/selectors';

import { HomeScreen } from '..';

// Keep suppressing handoff-window logouts for a short grace period after home mounts.
// A stale pre-handoff 401 can fire its async onTokenRefreshFail up to ~1s after we land
// on home; clearing the flag on mount reopens the logout race inside that window.
const HANDOFF_SUPPRESSION_GRACE_MS = 5000;

const HomeScreenSwitcher = (): ReactElement => {
  const dispatch = useDispatch();
  const isHandoffAuthenticated = useSelector(getIsHandoffAuthenticated);

  // Clear the handoff-authenticated flag so the 401 handler resumes normal operation
  // once the home screen has settled. The flag was set in CreateAccountCallbackScreen to
  // suppress spurious logouts from in-flight 401s during the handoff transition. We clear
  // it after a grace period (not on mount) so late-arriving stale 401s are still
  // suppressed, while keeping the window bounded — the flag is persisted, so it must not
  // remain true across app launches.
  useEffect(() => {
    if (!isHandoffAuthenticated) {
      return undefined;
    }
    const timer = setTimeout(() => {
      dispatch(setIsHandoffAuthenticated(false));
    }, HANDOFF_SUPPRESSION_GRACE_MS);
    return () => clearTimeout(timer);
  }, [isHandoffAuthenticated, dispatch]);

  useUpdateTitle();
  return getProductName() === ProductReferences.SLEEPIO ? (
    <Formik initialValues={[]} onSubmit={noOpFunction}>
      <SessionsScreen />
    </Formik>
  ) : (
    <HomeScreen />
  );
};

export { HomeScreenSwitcher };
